import { observer } from "mobx-react";
import * as React from "react";
import Localization from "../core/Localization";
import Button from "../coreui/Button";
import Dialog from "../coreui/Dialog";
import DialogActions from "../coreui/DialogActions";
import DialogContent from "../coreui/DialogContent";
import Markdown, { Justification } from "../coreui/Markdown";
import { TableVerticalLayoutProps } from "../coreui/Table";
import PaneRow, { WidgetValue } from "../models/PaneRow";
import { AccessLevel, ValueByBreakpoint } from "./Api";

interface ConfigProperties {
  dataId: string;
  dialogType: DialogType;
  justification: ValueByBreakpoint<Justification>;
  name: string;
  propagated?: TableVerticalLayoutProps;
}

export type DialogType = "Info Dialog" | "Help Dialog" | "Warning Dialog";

const dialogTypeConfig = {
  "Help Dialog": {
    color: "information",
    icon: "fas fa-question",
  },
  "Info Dialog": {
    color: "information",
    icon: "fas fa-info",
  },
  "Warning Dialog": {
    color: "warning",
    icon: "fas fa-exclamation",
  },
};

interface RuntimeProperties {
  accessLevel: AccessLevel;
  textValue: string;
  alternateText: string;
}

function getAccessibleButtonText(
  dialogType: string,
  alternateText: string
): string {
  // Function added for case where MarkdownDialogColumn uses this widget in
  // xs breakpoint but cannot add alternateText (runtime prop)
  if (!alternateText) {
    switch (dialogType) {
      case "Info Dialog":
        return Localization.getBuiltInMessage("DataTable.viewInfo");
      case "Help Dialog":
        return Localization.getBuiltInMessage("DataTable.viewHelp");
      case "Warning Dialog":
        return Localization.getBuiltInMessage("DataTable.viewWarning");
      default:
        return "";
    }
  } else {
    return alternateText;
  }
}

const MarkdownDialog = observer(
  (props: ConfigProperties): JSX.Element | null => {
    const [open, setOpen] = React.useState(false);

    let rowKey = null;
    if (props.propagated?.parentTable) {
      // Widget is rendered in the vertical layout of a grid.
      rowKey = props.propagated.rowKey;
    }
    const row = PaneRow.get(props.dataId, rowKey);
    if (row === null) {
      return null;
    }

    const widget = row.getWidgetT<WidgetValue, RuntimeProperties>(props.name);
    const runtimeProperties = widget.properties;

    if (
      runtimeProperties.accessLevel === AccessLevel.hidden ||
      !runtimeProperties
    ) {
      return null;
    }

    const dialogConfig = dialogTypeConfig[props.dialogType];

    const handleOpen = () => {
      setOpen(true);
    };

    const handleClose = () => {
      setOpen(false);
    };

    return (
      <>
        <Button
          aria-label={getAccessibleButtonText(
            props.dialogType,
            runtimeProperties.alternateText
          )}
          aria-hidden={false}
          color={dialogConfig.color}
          icon={dialogConfig.icon}
          onClick={handleOpen}
          size={"small"}
          fab
        />
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
          <DialogContent>
            <Markdown
              autoFocus
              justification={props.justification}
              value={runtimeProperties.textValue}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>
              {Localization.getBuiltInMessage("close")}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export default MarkdownDialog;
